import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

// Exported so the page can build matching FAQPage structured data from the
// exact same questions and answers that render on screen.
export const faqs = [
  {
    q: 'Do I need prior MetaTrader 5 experience?',
    a: 'No. If you can install a desktop application and follow a few on-screen steps, you can get the EA running. The three steps above assume no background in trading software — each action is done through standard MT5 menus.',
  },
  {
    q: 'Which broker or account type should I use?',
    a: 'Any MT5 account that offers the XAUUSD symbol in Market Watch will work. What matters is that algorithmic trading is permitted on the account and that the broker allows automated orders to be placed without manual confirmation.',
  },
  {
    q: 'Does my computer need to stay switched on?',
    a: 'Yes. The EA runs inside the MT5 terminal on your machine, so the terminal must remain open and connected while you want the system active. If the terminal closes, the EA stops trading until it is started again.',
  },
  {
    q: 'Can the EA run on more than one chart or account?',
    a: 'You can attach it to a separate XAUUSD chart on each account you want it to trade, but each instance is independent. Start with a single chart so you can confirm the behaviour matches what is documented here before scaling up.',
  },
  {
    q: 'What settings should I start with?',
    a: 'Begin with the default configuration supplied with your delivery. They are chosen so a first run behaves predictably. If you later adjust risk or lot size, change one setting at a time and observe the result before moving on.',
  },
  {
    q: 'Can I change the configuration while it is running?',
    a: 'Yes. Open the EA properties from the chart, adjust the inputs, and confirm. The new values take effect on the next action the EA takes — you do not need to remove the EA or restart the terminal.',
  },
];

function SetupFaq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="w-full bg-background-50 px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[880px] mx-auto">
        <Reveal>
          <div className="text-center">
            <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
              COMMON QUESTIONS
            </span>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-foreground-950 text-[28px] leading-[1.12] sm:text-[36px] lg:text-[42px]">
              Questions Before You Run It.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600 max-w-[560px] mx-auto">
              The things people usually ask before loading the EA onto a chart for the first time.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 rounded-lg border border-background-300 bg-background-100 overflow-hidden">
            {faqs.map((item, idx) => {
              const isOpen = open === idx;
              return (
                <div key={item.q} className="border-b border-background-200 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="group w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left cursor-pointer"
                  >
                    <span
                      className={`font-heading font-bold tracking-tight text-[15px] md:text-[16px] transition-colors duration-200 ${
                        isOpen ? 'text-primary-700' : 'text-foreground-950 group-hover:text-primary-700'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-background-300 group-hover:border-primary-300'
                      }`}
                    >
                      <i
                        className={`${
                          isOpen ? 'ri-subtract-line text-background-50' : 'ri-add-line text-foreground-500'
                        } text-[15px]`}
                      ></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 md:px-7 pb-6 text-[14px] leading-relaxed text-foreground-600 max-w-[720px]">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default SetupFaq;